/**
 * @fileoverview Article outline for FlexibleLayout Text sections
 * @module screen-view-v2/utils/semanticMarkup
 */

import type { LayoutSection } from '@segmently/schema-models/v2';
import type { ArticleOutline, ArticleOutlineHeading } from '@modules/screen-view-v2/context/ArticleRenderContext';
import { resolveLabel } from '@modules/screen-view-v2/utils/contentResolver';
import { toPlainText } from '@modules/screen-view-v2/utils/articleText';
import { createSlugAllocator } from '@modules/screen-view-v2/utils/articleSlug';

export type SemanticTextTag = 'h1' | 'h2' | 'h3' | 'p';

export interface SectionSemanticTags {
  title?: SemanticTextTag;
  subtitle?: SemanticTextTag;
  /** Slug id of the title when it renders as h2/h3 (only with a defaultLanguage, i.e. inside the article render context). */
  titleId?: string;
  subtitleId?: string;
}

export interface ResolveSemanticTagsOptions {
  /** Heading ids are slugged from this language's text (D5) — never the visitor's locale. Absent = no ids. */
  defaultLanguage?: string;
}

type OutlineSection = Pick<LayoutSection, 'id' | 'kind' | 'textContent' | 'semanticTag'>;
type Label = Parameters<typeof resolveLabel>[0];

const EXPLICIT_TAGS = new Set<string>(['h1', 'h2', 'h3', 'p']);
const isHeadingTag = (tag: SemanticTextTag | undefined): tag is 'h2' | 'h3' => tag === 'h2' || tag === 'h3';

/**
 * Semantic elements for the Text sections of an article, in render order.
 * - Explicit `semanticTag` (G2, PR11) applies to the title (to the subtitle when the title is blank);
 *   the subtitle of a titled section stays p; a second h1 renders as h2.
 * - Without it, inference (W2): the first non-blank title is the h1 (none when any section sets an
 *   explicit h1), later titles h2, subtitles p.
 * - With `options.defaultLanguage`, h2/h3 get stable, unique slug ids from that language's text.
 */
export function resolveArticleSemanticTags(
  sortedSections: ReadonlyArray<OutlineSection>,
  locale: string,
  options: ResolveSemanticTagsOptions = {},
): Map<string, SectionSemanticTags> {
  const tags = new Map<string, SectionSemanticTags>();
  const explicitH1 = sortedSections.some((section) => section.kind === 'Text' && section.semanticTag === 'h1');
  const nextId = options.defaultLanguage ? createSlugAllocator() : null;
  let hasH1 = false;

  for (const section of sortedSections) {
    if (section.kind !== 'Text' || !section.textContent) continue;
    const { title, subtitle } = section.textContent;
    const hasTitle = hasText(title, locale);
    const hasSubtitle = hasText(subtitle, locale);
    const entry: SectionSemanticTags = {};
    const explicit = typeof section.semanticTag === 'string' && EXPLICIT_TAGS.has(section.semanticTag)
      ? (section.semanticTag as SemanticTextTag) : undefined;

    if (explicit) {
      const tag: SemanticTextTag = explicit === 'h1' && hasH1 ? 'h2' : explicit;
      if (hasTitle) {
        entry.title = tag;
        if (hasSubtitle) entry.subtitle = 'p';
      } else if (hasSubtitle) {
        entry.subtitle = tag;
      }
      if (tag === 'h1' && (hasTitle || hasSubtitle)) hasH1 = true;
    } else {
      if (hasTitle) {
        entry.title = hasH1 || explicitH1 ? 'h2' : 'h1';
        if (entry.title === 'h1') hasH1 = true;
      }
      if (hasSubtitle) entry.subtitle = 'p';
    }

    if (nextId && options.defaultLanguage) {
      if (isHeadingTag(entry.title)) entry.titleId = nextId(headingSource(title, options.defaultLanguage));
      if (isHeadingTag(entry.subtitle)) entry.subtitleId = nextId(headingSource(subtitle, options.defaultLanguage));
    }
    if (entry.title || entry.subtitle) tags.set(section.id, entry);
  }

  return tags;
}

/** The h2/h3 headings (with ids) in render order; text in the render locale, markup stripped. */
export function buildArticleOutline(
  sortedSections: ReadonlyArray<Pick<LayoutSection, 'id' | 'kind' | 'textContent'>>,
  tags: ReadonlyMap<string, SectionSemanticTags>,
  locale: string,
): ArticleOutline {
  const headings: ArticleOutlineHeading[] = [];
  for (const section of sortedSections) {
    const entry = tags.get(section.id);
    if (!entry || section.kind !== 'Text' || !section.textContent) continue;
    if (isHeadingTag(entry.title) && entry.titleId) {
      headings.push({ sectionId: section.id, id: entry.titleId, tag: entry.title, text: toPlainText(resolveLabel(section.textContent.title, locale) ?? '') });
    }
    if (isHeadingTag(entry.subtitle) && entry.subtitleId) {
      headings.push({ sectionId: section.id, id: entry.subtitleId, tag: entry.subtitle, text: toPlainText(resolveLabel(section.textContent.subtitle, locale) ?? '') });
    }
  }
  return { headings };
}

function headingSource(label: Label, defaultLanguage: string): string {
  return toPlainText(resolveLabel(label, defaultLanguage) ?? '');
}

function hasText(label: Label, locale: string): boolean {
  const text = resolveLabel(label, locale);
  return typeof text === 'string' && text.trim().length > 0;
}
